import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { openDB } from 'idb'

const DB_NAME = 'sjp-voting-db'
const DB_VERSION = 1
const VOTE_QUEUE_STORE = 'voteQueue'
const SELECTIONS_STORE = 'selections'

export const useOfflineStore = defineStore('offline', () => {
  // State
  const isOnline = ref(navigator.onLine)
  const isChecking = ref(false)
  const queueLength = ref(0)
  const lastSyncAttempt = ref(null)
  const syncError = ref(null)

  // Getters
  const hasQueuedVotes = computed(() => queueLength.value > 0)
  const showOfflineBanner = computed(() => !isOnline.value || hasQueuedVotes.value)

  // Initialize IndexedDB
  const initDB = async () => {
    return openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Store for queued votes
        if (!db.objectStoreNames.contains(VOTE_QUEUE_STORE)) {
          db.createObjectStore(VOTE_QUEUE_STORE, {
            keyPath: 'id',
            autoIncrement: true
          })
        }
        
        // Store for selections (for recovery)
        if (!db.objectStoreNames.contains(SELECTIONS_STORE)) {
          db.createObjectStore(SELECTIONS_STORE, {
            keyPath: 'sessionId'
          })
        }
      }
    })
  }

  // Actions
  const setOnlineStatus = (status) => {
    isOnline.value = status
  }

  const checkConnectivity = async () => {
    isChecking.value = true
    
    try {
      // Try to ping the server
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/health/ping/`,
        {
          method: 'GET',
          signal: controller.signal,
          cache: 'no-store'
        }
      )
      
      clearTimeout(timeoutId)
      
      if (response.ok) {
        isOnline.value = true
        return true
      }
    } catch (err) {
      // Network error
    } finally {
      isChecking.value = false
    }
    
    isOnline.value = false
    return false
  }

  const queueVote = async (sessionId, votes, idempotencyKey) => {
    const db = await initDB()
    
    const queueItem = {
      sessionId,
      votes,
      idempotencyKey,
      timestamp: Date.now(),
      retryCount: 0,
      lastError: null
    }
    
    await db.add(VOTE_QUEUE_STORE, queueItem)
    await updateQueueLength()
    
    return queueItem
  }

  const getQueuedVotes = async () => {
    const db = await initDB()
    return db.getAll(VOTE_QUEUE_STORE)
  }

  const removeQueuedVote = async (id) => {
    const db = await initDB()
    await db.delete(VOTE_QUEUE_STORE, id)
    await updateQueueLength()
  }

  const updateQueuedVote = async (id, updates) => {
    const db = await initDB()
    const item = await db.get(VOTE_QUEUE_STORE, id)
    if (item) {
      await db.put(VOTE_QUEUE_STORE, { ...item, ...updates })
    }
  }

  const updateQueueLength = async () => {
    const db = await initDB()
    const count = await db.count(VOTE_QUEUE_STORE)
    queueLength.value = count
    return count
  }

  const saveSelections = async (sessionId, selections) => {
    const db = await initDB()
    await db.put(SELECTIONS_STORE, {
      sessionId,
      selections,
      savedAt: Date.now()
    })
  }

  const getSavedSelections = async (sessionId) => {
    const db = await initDB()
    const item = await db.get(SELECTIONS_STORE, sessionId)
    return item?.selections || null
  }

  const clearSavedSelections = async (sessionId) => {
    const db = await initDB()
    await db.delete(SELECTIONS_STORE, sessionId)
  }

  const processQueue = async (api) => {
    if (!isOnline.value) {
      return { processed: 0, failed: 0 }
    }
    
    lastSyncAttempt.value = Date.now()
    syncError.value = null
    
    const queuedVotes = await getQueuedVotes()
    let processed = 0
    let failed = 0
    
    for (const item of queuedVotes) {
      try {
        const payload = {
          session_id: item.sessionId,
          votes: item.votes,
          idempotency_key: item.idempotencyKey
        }
        
        const response = await api.post('/vote/submit/', payload)
        
        if (response.data.success) {
          await removeQueuedVote(item.id)
          processed++
        } else {
          throw new Error(response.data.message)
        }
      } catch (err) {
        // Update retry count
        const newRetryCount = (item.retryCount || 0) + 1
        
        if (newRetryCount >= 5) {
          // Max retries reached, remove from queue
          await removeQueuedVote(item.id)
          failed++
        } else {
          await updateQueuedVote(item.id, {
            retryCount: newRetryCount,
            lastError: err.message
          })
        }
      }
    }
    
    await updateQueueLength()
    return { processed, failed }
  }

  const clearAllQueue = async () => {
    const db = await initDB()
    const tx = db.transaction(VOTE_QUEUE_STORE, 'readwrite')
    await tx.objectStore(VOTE_QUEUE_STORE).clear()
    await tx.done
    await updateQueueLength()
  }

  // Initialize
  const initialize = async () => {
    await updateQueueLength()
    
    // Set up online/offline listeners
    window.addEventListener('online', () => {
      isOnline.value = true
    })
    
    window.addEventListener('offline', () => {
      isOnline.value = false
    })
  }

  return {
    // State
    isOnline,
    isChecking,
    queueLength,
    lastSyncAttempt,
    syncError,
    // Getters
    hasQueuedVotes,
    showOfflineBanner,
    // Actions
    initialize,
    setOnlineStatus,
    checkConnectivity,
    queueVote,
    getQueuedVotes,
    removeQueuedVote,
    updateQueuedVote,
    saveSelections,
    getSavedSelections,
    clearSavedSelections,
    processQueue,
    clearAllQueue
  }
})
